import { Injectable } from '@angular/core';
import { GmrUserInfo } from './gmr.models';

//
// The `Invantive.Iwid` cookie: the identity of the visitor, shared with the other Invantive
// applications under `.invantive.com`.
//
// Two identities live in it, per UI-19 of the workspace coding standards. `id` holds the IWID, the
// 36-character identifier of a person mastered in Estate; `em` holds the lower-cased e-mail address and
// is the provisional identity for as long as the IWID is unknown — which for a new customer it is,
// because Estate generates the IWID and the hourly ETL copies it into `gmr_ssn_users` afterwards.
//
// The two are never traded for one another: `em` is kept after the IWID arrives, as the identity this
// person was reported under earlier, and an `id` already present is never removed.
//
// Grew out of the "Determine IWID" block in gmr-app.js's startFunction success handler; see
// itgen/ai/IWID_IDENTITY.md for the whole mechanism.
//
interface IwidCookiePayload
{
  MetadataType?: string;
  id?: string | null;
  em?: string | null;
  lg?: string;
  gd?: string;
  ed?: string;
  oa?: string;
  oi?: string;
}

const COOKIE_NAME = 'Invantive.Iwid';

@Injectable({ providedIn: 'root' })
export class IwidCookieService
{
  resolveAndPersist(userInfo: GmrUserInfo): string | undefined
  {
    const current = this.read();
    const identity = userInfo.d2d_user_identity as string | undefined;

    if (!identity)
    {
      return this.identityOf(current);
    }

    this.write(identity, current, userInfo);

    return identity;
  }

  //
  // The identity a payload carries: the IWID when it holds one, otherwise the provisional identity on
  // the e-mail address, otherwise nothing. The order is the whole point of the two keys.
  //
  private identityOf(payload: IwidCookiePayload | null): string | undefined
  {
    if (payload?.id && payload.id.length === 36)
    {
      return 'IWID-' + payload.id;
    }

    if (payload?.em)
    {
      return 'EML-' + payload.em;
    }

    return undefined;
  }

  //
  // Matches the cookie name exactly rather than by substring, and tolerates an unparseable value.
  //
  private read(): IwidCookiePayload | null
  {
    const prefix = COOKIE_NAME + '=';
    const entry = document.cookie.split('; ').find(row => row.indexOf(prefix) === 0);

    if (!entry)
    {
      return null;
    }

    try
    {
      return JSON.parse(decodeURIComponent(entry.substring(prefix.length))) as IwidCookiePayload;
    }
    catch
    {
      return null;
    }
  }

  //
  // Written for a year, with the expiry moved forward as Invantive Cloud does: a visitor who keeps coming
  // back keeps the identifier instead of losing it a year after the first visit. Nothing is written
  // unless there is a reason to — the identity changed, or the expiry the cookie carries has drifted more
  // than a week behind where it should be — so an ordinary page view sets no cookie header.
  //
  private write(identity: string, current: IwidCookiePayload | null, userInfo: GmrUserInfo): void
  {
    const idCurrent = current?.id ?? null;
    const emCurrent = current?.em ?? null;

    let idSoll = idCurrent;
    let emSoll = emCurrent;

    if (identity.startsWith('IWID-'))
    {
      idSoll = identity.substring(5);
    }
    else if (identity.startsWith('EML-'))
    {
      emSoll = identity.substring(4).toLowerCase();
    }

    if (!idSoll && !emSoll)
    {
      return;
    }

    const dateCurrent = new Date();
    const dateNextYear = addOneYear(dateCurrent);

    const expiryCurrent = current?.ed ? new Date(current.ed) : null;

    const expiryDrifted = !expiryCurrent
                       || isNaN(expiryCurrent.getTime())
                       || (dateNextYear.getTime() - expiryCurrent.getTime()) >= 7 * 24 * 60 * 60 * 1000;

    if (current && idSoll === idCurrent && emSoll === emCurrent && !expiryDrifted)
    {
      return;
    }

    let languageCode = userInfo.usr_language;
    if (languageCode.length >= 3)
    {
      languageCode = languageCode.split('-')[0];
    }

    const payload: IwidCookiePayload =
    {
      MetadataType: 'IwidCookie'
    , id: idSoll
    , em: emSoll
    , lg: languageCode
    , gd: dateCurrent.toJSON()
    , ed: dateNextYear.toJSON()
    , oa: userInfo.elastic_apm_service_name
    , oi: userInfo.gui_ip_address_external
    };

    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}; expires=${dateNextYear.toUTCString()}; Secure; SameSite=Lax; path=/${cookieDomainAttribute()}`;
  }
}

function addOneYear(dateCurrent: Date): Date
{
  const dateCopy = new Date(dateCurrent);
  dateCopy.setFullYear(dateCopy.getFullYear() + 1);
  return dateCopy;
}

//
// The registrable domains this application is served from. One cookie can not span both, so the cookie
// is written for whichever of them the page's host belongs to and is then shared with the sibling hosts
// of that domain. A browser drops a cookie whose domain does not match the host without an error, which
// is why the domain is derived here rather than fixed: a fixed `.invantive.com` stored nothing on an
// invantive.eu host. A host under neither domain (a developer's machine) gets a host-only cookie.
//
// Exported because the Inspectlet cross-domain list in ThirdPartyIntegrationsService has to be built
// for the same domain: the identity cookie is what lets a session be stitched across hosts at all.
//
export const COOKIE_DOMAINS = ['invantive.com', 'invantive.eu'];

export function cookieDomainOfHost(host: string): string | undefined
{
  const hostLower = host.toLowerCase();

  return COOKIE_DOMAINS.find(candidate => hostLower === candidate || hostLower.endsWith('.' + candidate));
}

function cookieDomainAttribute(): string
{
  const domain = cookieDomainOfHost(window.location.hostname);

  return domain ? '; domain=.' + domain : '';
}
